/**
 * The pure half of usePermission: how a permission is read from a role's tree, with no React and
 * no store. usePermission wraps it in the store; code that must answer the same question outside a
 * hook (the Settings resolver, which the route guard also calls) uses it directly, so the two can
 * never disagree.
 */
import type {BackendRolePermissions} from '../utils/permissions'

/**
 * Module key aliases for permission resolution
 * Maps the keys used in usePermission calls to alternative keys that may exist
 * in different category permissions (Company vs Platform vs Agency)
 *
 * Pattern: The components check 'manage_companies' (Platform key) but Company admins
 * have 'company_manage_profile' with submodules like 'company_region' instead of 'region'
 */
const PERMISSION_MODULE_ALIASES: Record<string, string[]> = {
  manage_companies: ['company_manage_profile'],
  manage_agencies: ['manage_profile'],
}

const PERMISSION_SUBMODULE_ALIASES: Record<string, string[]> = {
  region: ['company_region'],
  timesheets: ['company_timesheets'],
  profile: ['company_profile'],
  invoices: ['company_invoices'],
  manage_orders: ['company_manage_orders'],
  agency_profile: ['profile'],
  managed_sites: ['managed_sites'],
  tax_compliance: ['tax_compliance'],
  banking: ['banking'],
  eor_details: ['eor_details'],
  payroll_setup: ['payroll_setup'],
  catalogs: ['catalogs'],
  affiliates: ['affiliates'],
  agency_documents: ['documents'],
  company_documents: ['documents'],
  agency_invoices: ['invoices'],
  agency_timesheets: ['timesheets'],
  agency_users_roles: ['users_roles'],
}

export interface PermissionCheck {
  /** Can user view this module/feature */
  canView: boolean
  /** Can user create new items in this module/feature */
  canCreate: boolean
  /** Can user edit existing items in this module/feature */
  canEdit: boolean
  /** Can user delete items in this module/feature */
  canDelete: boolean
  /** Is the permission data still loading */
  isLoading: boolean
  /**
   * True when the requested module/submodule was actually found in the
   * user's permission tree (explicit grant or explicit deny).
   * False when the lookup fell through without finding anything.
   *
   * Needed by aggregation hooks (e.g. useDocumentPermission) that combine
   * multiple aliased sources: the AND across sources must only consider
   * sources that were actually present in the user's tree, so an absent
   * alias doesn't incorrectly deny access that another aliased path grants.
   */
  matched: boolean
}

/** A node of a stored permission tree, at any depth (the stored type stops at two levels). */
export type PermissionNode = {
  key: string
  canView?: boolean
  canCreate?: boolean
  canEdit?: boolean
  canDelete?: boolean
  submodules?: PermissionNode[]
}

/** Convert a permission node into a PermissionCheck result */
export const toPermissionCheck = (node: PermissionNode): PermissionCheck => ({
  canView: node.canView ?? false,
  canCreate: node.canCreate ?? false,
  canEdit: node.canEdit ?? false,
  canDelete: node.canDelete ?? false,
  isLoading: false,
  matched: true,
})

/**
 * Search a submodules array for a key, falling back to submodule aliases.
 * Searches recursively through sub-submodules at any depth.
 */
const findSubmoduleWithAliases = (
  submodules: PermissionNode[],
  submoduleKey: string,
): PermissionNode | undefined => {
  // Direct match first
  const direct = submodules.find((s) => s.key === submoduleKey)
  if (direct) return direct

  // Try aliased submodule keys (e.g. 'region' → 'company_region')
  const aliases = PERMISSION_SUBMODULE_ALIASES[submoduleKey]
  if (aliases) {
    for (const alias of aliases) {
      const found = submodules.find((s) => s.key === alias)
      if (found) return found
    }
  }

  // Recursive search in sub-submodules
  for (const sub of submodules) {
    if (sub.submodules?.length) {
      const nested = findSubmoduleWithAliases(sub.submodules, submoduleKey)
      if (nested) return nested
    }
  }
  return undefined
}

/**
 * Given a module, an optional submodule key, and an optional sub-submodule key,
 * resolve the permission. Returns PermissionCheck or undefined if a requested
 * child was not found.
 */
const resolveModulePermission = (
  mod: PermissionNode,
  submoduleKey?: string,
  subSubmoduleKey?: string,
): PermissionCheck | undefined => {
  if (submoduleKey && mod.submodules && mod.submodules.length > 0) {
    const sub = findSubmoduleWithAliases(mod.submodules, submoduleKey)
    if (!sub) return undefined
    if (subSubmoduleKey) {
      if (!sub.submodules || sub.submodules.length === 0) return undefined
      const subSub = findSubmoduleWithAliases(sub.submodules, subSubmoduleKey)
      return subSub ? toPermissionCheck(subSub) : undefined
    }
    return toPermissionCheck(sub)
  }
  if (!submoduleKey || !mod.submodules || mod.submodules.length === 0) {
    const result = toPermissionCheck(mod)
    if (!result.canView && mod.submodules?.some((s) => s.canView)) {
      return {...result, canView: true}
    }
    return result
  }
  return undefined
}

/**
 * Fallback function to check legacy accessList format
 * Used when modulePermissions is not available
 */
const checkLegacyAccessList = (
  accessList: Array<{entityType: string; scope: string; operations: string[]}> | undefined,
  moduleKey: string,
): PermissionCheck => {
  const unmatchedDefault: PermissionCheck = {
    canView: false,
    canCreate: false,
    canEdit: false,
    canDelete: false,
    isLoading: false,
    matched: false,
  }

  if (!accessList || !Array.isArray(accessList)) {
    return unmatchedDefault
  }

  // Map moduleKey to entityType
  const entityTypeMap: Record<string, string> = {
    manage_companies: 'Company',
    manage_agencies: 'Agency',
    manage_orders: 'ServiceOrder',
    job_orders: 'JobOrder',
    candidates: 'Applicant',
    users_roles: 'User',
    payroll: 'Timecard',
    positions: 'Position',
  }

  const entityType = entityTypeMap[moduleKey]
  if (!entityType) {
    return unmatchedDefault
  }

  const accessEntry = accessList.find(
    (a) => a.entityType.toLowerCase() === entityType.toLowerCase(),
  )

  if (!accessEntry) {
    return unmatchedDefault
  }

  const operations = accessEntry.operations.map((op) => op.toLowerCase())

  return {
    canView: operations.includes('read'),
    canCreate: operations.includes('create'),
    canEdit: operations.includes('update') || operations.includes('updatestatus'),
    canDelete: operations.includes('delete'),
    isLoading: false,
    matched: true,
  }
}

/**
 * What usePermission answers for these keys, given the role's permissions as the store holds them.
 */
export const resolvePermission = (
  backendRolePermissions: BackendRolePermissions | null | undefined,
  moduleKey: string,
  submoduleKey?: string,
  subSubmoduleKey?: string,
): PermissionCheck => {
  const unmatchedDefault: PermissionCheck = {
    canView: false,
    canCreate: false,
    canEdit: false,
    canDelete: false,
    isLoading: !backendRolePermissions,
    matched: false,
  }

  if (!backendRolePermissions) {
    return unmatchedDefault
  }

  const modulePermissions: PermissionNode[] | undefined = backendRolePermissions.modulePermissions
  if (!modulePermissions || !Array.isArray(modulePermissions)) {
    return checkLegacyAccessList(backendRolePermissions.accessList, moduleKey)
  }

  // 1. Direct module match
  const directModule = modulePermissions.find((m) => m.key === moduleKey)
  if (directModule) {
    const result = resolveModulePermission(directModule, submoduleKey, subSubmoduleKey)
    if (result) return result
    // Parent module was found but the requested submodule wasn't - this is
    // an explicit deny (the role knows about the parent module but didn't
    // grant this specific submodule), so matched=true with all flags false.
    if (submoduleKey) return {...unmatchedDefault, matched: true}
  }

  // 2. Try module key aliases (e.g. 'manage_companies' → 'company_manage_profile')
  const moduleAliases = PERMISSION_MODULE_ALIASES[moduleKey]
  if (moduleAliases) {
    for (const aliasKey of moduleAliases) {
      const aliasModule = modulePermissions.find((m) => m.key === aliasKey)
      if (!aliasModule) continue
      const result = resolveModulePermission(aliasModule, submoduleKey, subSubmoduleKey)
      if (result) return result
    }
  }

  // 3. Search all submodules (and sub-submodules) for the moduleKey itself
  const findNodeRecursive = (nodes: PermissionNode[], key: string): PermissionNode | undefined => {
    for (const n of nodes) {
      if (n.key === key) return n
      if (n.submodules?.length) {
        const found = findNodeRecursive(n.submodules, key)
        if (found) return found
      }
    }
    return undefined
  }
  const nestedMatch = findNodeRecursive(modulePermissions, moduleKey)
  if (nestedMatch) return toPermissionCheck(nestedMatch)

  return unmatchedDefault
}
